import { Columns } from '../columns';
import { EmptyList } from '../emptyList';
import React, { PropsWithChildren } from 'react';
import { Center } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { useInfiniteScroll } from 'src/shared/hooks';
import { CatalogListItem, CatalogListLoader } from '.';
import { CatalogListItemButton, CatalogListItemModel } from './catalogListItemModel';
import { ZTooltip } from '../ztooltip';

export interface CatalogListContentProps<T> {
    loadNext: () => void;
    hasNext: boolean;
    isLoadingNext: boolean;
    readonly models: T[];
    onItemClick: (model: T, hideAlreadyConfiguredBadge?: boolean) => void;
    scrollContainer?: HTMLElement | null;
    noItemsIcon?: React.ReactNode | null;
    noItemsMessage?: string;
    noItemsSubMessage?: React.ReactNode;
    className?: string;
    onCatalogListItemButtonClick?: (button: CatalogListItemButton, model: T) => void;
    columns?: { xs: number; md: number; lg: number };
    hideAlreadyConfiguredBadge?: boolean;
    disableProductCode?: string;
    disabledIds?: Set<string>;
    disabledTooltip?: string;
    tooltipHtml?: string;
    isDamProductsCatalog?: boolean;
    noItemsHideIcon?: boolean;
}

export const CatalogListContent = <T extends CatalogListItemModel = CatalogListItemModel>({
    loadNext,
    hasNext,
    isLoadingNext,
    models,
    onItemClick,
    scrollContainer,
    noItemsIcon,
    noItemsMessage,
    noItemsSubMessage,
    className,
    columns,
    onCatalogListItemButtonClick,
    hideAlreadyConfiguredBadge,
    disableProductCode,
    disabledIds,
    disabledTooltip,
    tooltipHtml,
    isDamProductsCatalog,
    noItemsHideIcon
}: PropsWithChildren<CatalogListContentProps<T>>) => {
    useInfiniteScroll(loadNext, hasNext, scrollContainer, isLoadingNext);

    return (
        <div className={className}>
            {models.length == 0 && (
                <Center>
                    <EmptyList
                        icon={noItemsIcon}
                        hideIcon={noItemsHideIcon}
                        title={noItemsMessage || T._('Nothing found.', 'Admin')}
                        subTitle={noItemsSubMessage}
                    />
                </Center>
            )}

            {models.length > 0 && (
                <Columns
                    $xs={columns?.xs ?? 2}
                    $md={columns?.md ?? 5}
                    $lg={columns?.lg ?? 5}
                    $gap={30}
                    css={`
                        margin-bottom: 30px;
                    `}
                >
                    {models.map((model) => {
                        const isDisabled = disabledIds?.has(model.id);
                        const item = (
                            <CatalogListItem
                                selected={model.selected}
                                key={model.id.toString()}
                                model={model}
                                onClick={(hideAlreadyConfiguredBadge) => onItemClick(model, hideAlreadyConfiguredBadge)}
                                onCatalogListItemButtonClick={(button) => onCatalogListItemButtonClick?.(button, model)}
                                hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}
                                disableProductCode={isDisabled ? model.id : disableProductCode}
                            />
                        );

                        if (isDisabled && disabledTooltip) {
                            return (
                                <ZTooltip key={model.id.toString()} html={<span>{disabledTooltip}</span>} position={'top'} addContainer>
                                    {item}
                                </ZTooltip>
                            );
                        }

                        return item;
                    })}
                </Columns>
            )}
            {isLoadingNext && CatalogListLoader}
        </div>
    );
};
